import { type Edge, MarkerType, type Node as ReactFlowNode } from '@xyflow/react';
import type { AgentSource } from '../../../api/events-client/events-client.type';
import type {
  AgentSessionNode,
  AgentTurnNode,
  CommitNode,
  PrCommentNode,
  UnifiedProcessGraphData,
} from '../../../api/unified-process-graph-client/unified-process-graph-client.type';
import { formatCurrency } from '../../../helpers/currency-helpers';
import { AGENT_SOURCES_ICONS } from '../../agentic-process/agentic-process.type';
import { fmtDuration, fmtDurationLong, turnDisplayNumber } from '../../agent-miner/shared/formatters';
import { PHASE_COLORS } from '../../flora-agent/artifacts/artifact-colors';
import {
  AGENT_SESSION_NODE_PREFIX,
  BRANCH_LANES,
  COMMENT_PREVIEW_LENGTH,
  COMMIT_HANDOFF_X,
  COMMIT_LABEL_MAX_WIDTH,
  COMMIT_MESSAGE_PREVIEW_LENGTH,
  COMMIT_MESSAGE_PREVIEW_LINES,
  HANDOFF_X,
  LANE_STYLES,
  MORE_TURNS_NODE_PREFIX,
  PR_COLUMN_WIDTH,
  PR_HANDOFF_X,
  PR_LIFECYCLE_STACK_STEP,
  ROW_SPACING,
  STATUS_X,
  TOOL_CALL_EVENT_TYPES,
  TRACK_BOTTOM_PADDING,
  TRACK_CENTER_X,
  TRACK_EVENT_STEP,
  TRACK_GAP_LABEL_HEIGHT,
  TRACK_GAP_LABEL_OFFSET_X,
  TRACK_LONG_GAP_MS,
  TRACK_MARKER_SIZE,
  TRACK_TOP_OFFSET,
  TRACK_WIDTH,
  TURN_LABEL_MAX_WIDTH,
  TURN_LANES,
  TURN_MARKER_NODE_PREFIX,
  TURN_NODE_PREFIX,
  TURN_PREVIEW_LIMIT,
  TURN_PROMPT_PREVIEW_LENGTH,
} from './unified-process-graph.constants';
import { InteractiveNodeKind, TurnExpansion } from './unified-process-graph.type';
import type {
  CommentPreview,
  CommitPreview,
  SessionInterval,
  TimelineEntry,
  TrackGapLabel,
  TrackMarker,
  TurnBlock,
  TurnTiming,
} from './unified-process-graph.type';

/**
 * Formats the time elapsed between two graph events (lane edges, status
 * transitions, task cycle time). Anything under a minute, including a negative
 * gap from out-of-order timestamps, reads "<1m".
 *
 * @param {number} ms - Elapsed time, in milliseconds
 * @returns {string} "<1m", else a label such as "14m", "5h 20m" or "2d 4h"; "—" when `ms` is NaN
 * @example
 * formatElapsed(30_000); // '<1m'
 * formatElapsed(14 * 60_000); // '14m'
 */
const formatElapsed = (ms: number): string => {
  if (Number.isNaN(ms) || ms >= 60_000) return fmtDurationLong(ms);
  return '<1m';
};

/**
 * Computes key overview metrics for a unified process graph.
 *
 * Metrics calculated:
 *   - Cycle Time: Duration between earliest and latest status node timestamps.
 *   - Agent Cost: Sum of all agent session costs in USD.
 *   - Commits: Total number of commits across all SCM nodes.
 *   - Code Changes: Total additions and deletions across SCM nodes.
 *
 * @param {UnifiedProcessGraphData} graph - The unified process graph data object.
 * @returns {Object} An object containing cycleTimeLabel, costLabel, commitsLabel, and codeChangesLabel.
 */
const computeOverviewMetrics = (
  graph: UnifiedProcessGraphData,
): { cycleTimeLabel: string; costLabel: string; commitsLabel: string; codeChangesLabel: string } => {
  // Task Cycle Time: first to last status node timestamp
  const statusTimestamps = graph.status_nodes
    .map((n) => new Date(n.timestamp).getTime())
    .filter((t) => !Number.isNaN(t));
  let cycleTimeLabel = '-';
  if (statusTimestamps.length >= 2) {
    const minTs = Math.min(...statusTimestamps);
    const maxTs = Math.max(...statusTimestamps);
    cycleTimeLabel = formatElapsed(maxTs - minTs);
  }

  // Agent Cost: sum of all session costs
  let totalCost = 0;
  for (const session of graph.agent_session_nodes) {
    if (session.total_cost_usd) {
      totalCost += Number.parseFloat(session.total_cost_usd);
    }
  }
  const costLabel = totalCost > 0 ? formatCurrency(totalCost) : '-';

  // Commits: total across all PRs
  let totalCommits = 0;
  for (const scm of graph.scm_nodes) {
    totalCommits += scm.commits.length;
  }
  const commitsLabel = `${totalCommits}`;

  // Code Changes: total additions and deletions
  let totalAdditions = 0;
  let totalDeletions = 0;
  for (const scm of graph.scm_nodes) {
    totalAdditions += scm.additions ?? 0;
    totalDeletions += scm.deletions ?? 0;
  }
  const codeChangesLabel = totalAdditions > 0 || totalDeletions > 0 ? `+${totalAdditions} / -${totalDeletions}` : '-';

  return { cycleTimeLabel, costLabel, commitsLabel, codeChangesLabel };
};

/**
 * Converts an agent session graph node id (`session_<uuid>`) into the raw
 * session id expected by the agent-sessions API.
 *
 * @param {string} nodeId - Agent session node id from the unified process graph
 * @returns {string} The session id with the node prefix removed; unprefixed ids are returned unchanged
 */
const toAgentSessionId = (nodeId: string): string =>
  nodeId.startsWith(AGENT_SESSION_NODE_PREFIX) ? nodeId.slice(AGENT_SESSION_NODE_PREFIX.length) : nodeId;

/**
 * Builds the hover preview for a PR comment node from its first comment: the
 * body flattened to one paragraph and trimmed, plus how many comments remain.
 * The count comes from the node (`loop_count` when merged), not the comment
 * list, since the API keeps only some comments' text on merged nodes.
 *
 * @param {PrCommentNode} comment - The comment node from the API
 * @returns {CommentPreview} The preview shown in the node's tooltip
 */
const buildCommentPreview = (comment: PrCommentNode): CommentPreview => {
  const [first] = comment.comments ?? [];
  const body = (first?.body ?? '').replace(/\s+/g, ' ').trim();
  const total = comment.loop_count ?? comment.count;
  return {
    author: first?.author ?? null,
    path: first?.path ?? null,
    body: body.length > COMMENT_PREVIEW_LENGTH ? `${body.slice(0, COMMENT_PREVIEW_LENGTH - 1).trimEnd()}\u2026` : body,
    remaining: Math.max(0, total - 1),
  };
};

/**
 * Builds the card and hover content for a commit node: its short hash, the
 * first non-empty line of the message as the subject, the whole message
 * (trimmed and capped in length and lines) and its author.
 *
 * @param {CommitNode} commit - The commit from the API
 * @returns {CommitPreview} The commit's preview
 * @example
 * buildCommitPreview({ hash: 'a1b2c3d4', message: 'Fix login\n\nDetails', author: null, ... });
 * // { hash: 'a1b2c3d4', subject: 'Fix login', message: 'Fix login\n\nDetails', author: null }
 */
const buildCommitPreview = (commit: CommitNode): CommitPreview => {
  const message = (commit.message ?? '').trim();
  const lines = message.split('\n');
  const subject = lines.find((line) => line.trim())?.trim() ?? '';
  // Cap lines first, then characters, so neither a long list nor one long line outgrows the tooltip.
  const kept = lines.slice(0, COMMIT_MESSAGE_PREVIEW_LINES).join('\n');
  const capped = kept.length > COMMIT_MESSAGE_PREVIEW_LENGTH ? kept.slice(0, COMMIT_MESSAGE_PREVIEW_LENGTH - 1) : kept;
  return {
    hash: commit.hash ?? '',
    subject,
    message: capped.length < message.length ? `${capped.trimEnd()}\u2026` : message,
    author: commit.author ?? null,
  };
};

/**
 * Collapses whitespace in a turn's prompt and truncates it for a graph label.
 *
 * @param {string | null} prompt - The user prompt that started the turn
 * @returns {string} A single-line preview, or an empty string when there is no prompt
 */
const formatTurnPromptPreview = (prompt: string | null): string => {
  const singleLine = (prompt ?? '').replace(/\s+/g, ' ').trim();
  if (singleLine.length <= TURN_PROMPT_PREVIEW_LENGTH) return singleLine;
  return `${singleLine.slice(0, TURN_PROMPT_PREVIEW_LENGTH - 1).trimEnd()}\u2026`;
};

/**
 * Resolves when each of a session's turns ran, ordered by turn number. A turn
 * with no parseable start is placed where the previous one ended, and no turn
 * starts before its session, so every timing is usable on the time track.
 *
 * @param {AgentSessionNode} session - The session whose turns are timed
 * @param {number} sessionTimestamp - The session's start, in epoch ms
 * @returns {TurnTiming[]} One timing per turn, in turn order
 */
const buildTurnTimings = (session: AgentSessionNode, sessionTimestamp: number): TurnTiming[] => {
  const turns = [...session.turns].sort((a, b) => a.turn_number - b.turn_number);
  let previousEnd = sessionTimestamp;
  return turns.map((turn) => {
    const parsedStart = turn.started_at ? new Date(turn.started_at).getTime() : Number.NaN;
    const startMs = Math.max(sessionTimestamp, Number.isNaN(parsedStart) ? previousEnd : parsedStart);
    const parsedEnd = turn.ended_at ? new Date(turn.ended_at).getTime() : Number.NaN;
    const durationEnd = turn.duration_ms != null ? startMs + turn.duration_ms : Number.NaN;
    const rawEnd = Number.isNaN(durationEnd) ? parsedEnd : durationEnd;
    const endMs = Number.isNaN(rawEnd) ? startMs : Math.max(startMs, rawEnd);
    previousEnd = endMs;
    return { turnId: turn.id, turnNumber: turn.turn_number, startMs, endMs };
  });
};

/**
 * Counts a turn's tool calls, including failed ones.
 *
 * @param {AgentTurnNode} turn - The turn from the unified process graph
 * @returns {number} How many of the turn's events are tool calls
 */
const countToolCalls = (turn: AgentTurnNode): number =>
  turn.events.filter((event) => TOOL_CALL_EVENT_TYPES.has((event.event_type ?? '').toUpperCase())).length;

/**
 * Builds the timeline entries for an expanded session's turn cards.
 *
 * Responsibilities:
 * 1. Label each turn with its number, cost and a prompt preview
 * 2. Cap the cards at `TURN_PREVIEW_LIMIT` in preview mode, adding a "+N more" card
 *    that points at the first hidden turn's segment
 * 3. Bucket every card with its session so the group lays out beneath it
 *
 * @param {AgentSessionNode} session - The session whose turns are shown
 * @param {TurnTiming[]} timings - The session's turn timings, in turn order
 * @param {number} sessionTimestamp - The session's start, in epoch ms
 * @param {TurnExpansion} expansion - Whether to show the preview or every turn
 * @returns {TimelineEntry[]} Turn cards followed by an optional "+N more" card
 */
const buildTurnEntries = (
  session: AgentSessionNode,
  timings: TurnTiming[],
  sessionTimestamp: number,
  expansion: TurnExpansion,
): TimelineEntry[] => {
  const sessionId = toAgentSessionId(session.id);
  const turnsById = new Map(session.turns.map((turn) => [turn.id, turn]));
  const visibleTimings = expansion === TurnExpansion.All ? timings : timings.slice(0, TURN_PREVIEW_LIMIT);

  // Timestamps only order entries in the global sort; nudge them strictly
  // increasing so equal start times keep turn order.
  let previousTimestamp = sessionTimestamp;
  const turnEntries: TimelineEntry[] = visibleTimings.map((timing) => {
    const turn = turnsById.get(timing.turnId);
    const timestamp = Math.max(timing.startMs, previousTimestamp + 1);
    previousTimestamp = timestamp;

    // Steps are evenly spaced, so the card carries the turn's duration.
    const durationMs = timing.endMs - timing.startMs;
    const durationLabel = durationMs > 0 ? ` \u00b7 ${fmtDuration(durationMs)}` : '';
    const toolCalls = turn ? countToolCalls(turn) : 0;
    const toolsLabel = toolCalls > 0 ? ` \u00b7 ${toolCalls} tool${toolCalls !== 1 ? 's' : ''}` : '';
    const costLabel = turn?.cost_usd ? ` \u00b7 $${parseFloat(turn.cost_usd).toFixed(2)}` : '';
    const prompt = formatTurnPromptPreview(turn?.user_prompt ?? null);

    return {
      timestamp,
      bucketTimestamp: sessionTimestamp,
      lane: 'agent_turn',
      id: `${TURN_NODE_PREFIX}${timing.turnId}`,
      label: `Turn ${turnDisplayNumber(timing.turnNumber)}${durationLabel}${toolsLabel}${costLabel}${prompt ? `\n${prompt}` : ''}`,
      parentId: session.id,
      sessionId,
      turnId: timing.turnId,
      trackTimeMs: timing.startMs,
    };
  });

  const hiddenCount = timings.length - visibleTimings.length;
  if (hiddenCount > 0) {
    turnEntries.push({
      timestamp: previousTimestamp + 1,
      bucketTimestamp: sessionTimestamp,
      lane: 'agent_turn_more',
      id: `${MORE_TURNS_NODE_PREFIX}${session.id}`,
      label: `+${hiddenCount} more turn${hiddenCount !== 1 ? 's' : ''}`,
      parentId: session.id,
      sessionId,
      // The hidden turns start here; the card is always the track's last step.
      trackTimeMs: timings[visibleTimings.length].startMs,
    });
  }

  return turnEntries;
};

/**
 * Formats the time between two consecutive track steps: seconds and minutes
 * for short gaps ("45s", "3m 12s"), hours and days for long ones ("5h 20m", "2d 4h").
 *
 * @param {number} ms - Time between the steps, in milliseconds
 * @returns {string} The gap label
 */
const formatTrackGap = (ms: number): string => (ms < TRACK_LONG_GAP_MS ? fmtDuration(ms) : fmtDurationLong(ms));

/**
 * Resolves when a session stopped running: its `ended_at`, else the end of its
 * last turn (when its timings are known), else its start.
 *
 * @param {AgentSessionNode} session - The session
 * @param {number} sessionTimestamp - The session's start, in epoch ms
 * @param {TurnTiming[] | undefined} timings - The session's turn timings, when expanded
 * @returns {number} The session's end, in epoch ms
 */
const resolveSessionEnd = (
  session: AgentSessionNode,
  sessionTimestamp: number,
  timings: TurnTiming[] | undefined,
): number => {
  const endedAt = session.ended_at ? new Date(session.ended_at).getTime() : Number.NaN;
  if (!Number.isNaN(endedAt)) return Math.max(sessionTimestamp, endedAt);
  return (timings ?? []).reduce((latest, timing) => Math.max(latest, timing.endMs), sessionTimestamp);
};

/**
 * Lays out a task-event gap that contains at least one expanded session.
 *
 * Responsibilities:
 * 1. Walk sessions and commits in time order, one row apart
 * 2. Turn each expanded session into a track: its turn cards, and the commits
 *    it made while running, as equal steps in time order, with the real time
 *    between steps as labels; "+N more" is always the last step
 * 3. Leave a commit made while another session was also running in normal
 *    order below the block, since it can't be attributed to this session
 * 4. Defer sessions that start during a block until after it, so they never
 *    land on the track
 *
 * @param {TimelineEntry[]} items - Sessions, turn cards and commits in the gap
 * @param {SessionInterval[]} sessionIntervals - Every session's running interval
 * @returns {{ offsets: Map<string, number>, blocks: TurnBlock[], height: number }}
 *   Item y offsets and tracks relative to the gap's top, and the gap's total height
 */
const layoutTemporalGap = (items: TimelineEntry[], sessionIntervals: SessionInterval[]) => {
  const cardsBySession = new Map<string, TimelineEntry[]>();
  const queue: TimelineEntry[] = [];
  items.forEach((item) => {
    if (TURN_LANES.has(item.lane) && item.parentId) {
      const cards = cardsBySession.get(item.parentId) ?? [];
      cards.push(item);
      cardsBySession.set(item.parentId, cards);
    } else {
      queue.push(item);
    }
  });
  queue.sort((a, b) => a.timestamp - b.timestamp);

  const runsOnlyOn = (sessionId: string, ms: number) =>
    !sessionIntervals.some((other) => other.id !== sessionId && other.startMs <= ms && ms <= other.endMs);

  const offsets = new Map<string, number>();
  const blocks: TurnBlock[] = [];
  let y = 0;
  let cursor = 0;
  while (cursor < queue.length) {
    const item = queue[cursor++];
    y += ROW_SPACING;
    offsets.set(item.id, y);
    if (!item.turnTimings) continue;

    const cards = cardsBySession.get(item.id) ?? [];
    const moreCard = cards.find((card) => card.lane === 'agent_turn_more');
    const turnCards = cards.filter((card) => card.lane === 'agent_turn');
    const lastTimingEnd = item.turnTimings.reduce((latest, timing) => Math.max(latest, timing.endMs), item.timestamp);
    // With turns hidden behind "+N more", the track stops where they begin.
    const windowEndMs = moreCard?.trackTimeMs ?? lastTimingEnd;

    // Pull the items that happened during this session's window out of the queue.
    const absorbedCommits: TimelineEntry[] = [];
    const deferred: TimelineEntry[] = [];
    // Once one of a PR's commits can't go on the track, its later commits stay
    // off it too; otherwise a later commit would be drawn above an earlier one.
    const prsLeftOffTrack = new Set<string>();
    while (cursor < queue.length && queue[cursor].timestamp <= windowEndMs) {
      const next = queue[cursor++];
      const prKey = next.parentId ?? '';
      if (next.lane === 'commit' && !prsLeftOffTrack.has(prKey) && runsOnlyOn(item.id, next.timestamp)) {
        absorbedCommits.push(next);
      } else {
        if (next.lane === 'commit') prsLeftOffTrack.add(prKey);
        deferred.push(next);
      }
    }
    queue.splice(cursor, 0, ...deferred);

    const timeOf = (entry: TimelineEntry) => entry.trackTimeMs ?? entry.timestamp;
    const steps = [...turnCards, ...absorbedCommits].sort((a, b) => timeOf(a) - timeOf(b) || a.timestamp - b.timestamp);
    if (moreCard) steps.push(moreCard);

    const trackTop = y + TRACK_TOP_OFFSET;
    const markers: TrackMarker[] = [];
    const gapLabels: TrackGapLabel[] = [];
    steps.forEach((step, index) => {
      const stepY = trackTop + index * TRACK_EVENT_STEP;
      // Node y is the dot's top; centre the 10px dot on the step.
      offsets.set(step.id, stepY - 5);
      if (step.lane !== 'commit') {
        const timing = item.turnTimings?.find((candidate) => candidate.turnId === step.turnId);
        markers.push({
          cardId: step.id,
          sessionId: item.sessionId ?? '',
          y: stepY,
          ...(timing && {
            turnId: timing.turnId,
            turnNumber: timing.turnNumber,
            durationMs: timing.endMs - timing.startMs,
          }),
        });
      }
      if (index > 0) {
        const gapMs = Math.max(0, timeOf(step) - timeOf(steps[index - 1]));
        gapLabels.push({
          y: stepY - TRACK_EVENT_STEP / 2,
          label: formatTrackGap(gapMs),
          isLong: gapMs >= TRACK_LONG_GAP_MS,
        });
      }
    });

    const trackHeight = Math.max(0, (steps.length - 1) * TRACK_EVENT_STEP);
    blocks.push({ sessionNodeId: item.id, trackTop, trackHeight, markers, gapLabels });
    y = trackTop + trackHeight + TRACK_BOTTOM_PADDING;
  }

  return { offsets, blocks, height: y + ROW_SPACING };
};

/**
 * Flatten all events from all sources into a single sorted timeline.
 * Expanded sessions also contribute their turn cards (and a "+N more" card when
 * the preview is capped) and a time block: see `layoutTemporalGap`.
 *
 * @param {UnifiedProcessGraphData} data - The graph data from the API
 * @param {ReadonlyMap<string, TurnExpansion>} turnExpansion - Expanded sessions, keyed by session node id
 * @returns {{ entries: TimelineEntry[], yPositions: Map<string, number>, turnBlocks: TurnBlock[] }}
 */
const buildTimeline = (
  data: UnifiedProcessGraphData,
  turnExpansion: ReadonlyMap<string, TurnExpansion>,
): { entries: TimelineEntry[]; yPositions: Map<string, number>; turnBlocks: TurnBlock[] } => {
  const entries: TimelineEntry[] = [];

  // Task events
  data.task_event_nodes.forEach((node) => {
    entries.push({
      timestamp: new Date(node.timestamp).getTime(),
      lane: 'task_event',
      id: node.id,
      label: node.label,
    });
  });

  // Agent sessions + turns (events injected if expanded)
  data.agent_session_nodes.forEach((session) => {
    if (session.timestamp) {
      const sourceIcon = AGENT_SOURCES_ICONS[session.source as AgentSource];
      const turnCount = session.turns.length;
      const costLabel = session.total_cost_usd ? `$${parseFloat(session.total_cost_usd).toFixed(2)}` : '';
      const detailParts = `Agent session\n${turnCount} turn${turnCount !== 1 ? 's' : ''} \u00b7 ${costLabel}`.trim();

      const sessionTimestamp = new Date(session.timestamp).getTime();
      const expansion = turnExpansion.get(session.id);
      // An unparseable start can't anchor a time scale, so such a session stays collapsed.
      const timings =
        expansion && !Number.isNaN(sessionTimestamp) ? buildTurnTimings(session, sessionTimestamp) : undefined;
      entries.push({
        timestamp: sessionTimestamp,
        lane: 'agent_session',
        id: session.id,
        label: detailParts,
        icon: sourceIcon,
        sessionId: toAgentSessionId(session.id),
        endTimestamp: resolveSessionEnd(session, sessionTimestamp, timings),
        ...(timings && timings.length > 0 && { turnTimings: timings }),
      });

      if (expansion && timings && timings.length > 0) {
        entries.push(...buildTurnEntries(session, timings, sessionTimestamp, expansion));
      }
    }
  });

  // Build PR index map — each PR gets a column index
  const prIndexMap: Map<string, number> = new Map();
  data.scm_nodes.forEach((scmNode, index) => {
    prIndexMap.set(scmNode.id, index);
  });

  // SCM: PR lifecycle (opened/merged), commits, and PR activity (comments/reviews)
  data.scm_nodes.forEach((scmNode) => {
    const prIndex = prIndexMap.get(scmNode.id) ?? 0;

    // PR opened → pr_lifecycle lane
    entries.push({
      timestamp: new Date(scmNode.timestamp).getTime(),
      lane: 'pr_lifecycle',
      id: scmNode.id,
      label: 'PR opened',
      parentId: `pr_lifecycle_${scmNode.id}`,
      prIndex,
    });

    // PR merged → pr_lifecycle lane
    if (scmNode.merged_at) {
      entries.push({
        timestamp: new Date(scmNode.merged_at).getTime(),
        lane: 'pr_lifecycle',
        id: `${scmNode.id}_merged`,
        label: 'PR closed',
        parentId: `pr_lifecycle_${scmNode.id}`,
        prIndex,
      });
    }

    // Repo header node above commits
    if (scmNode.repository_name && scmNode.commits.length > 0) {
      const firstCommitTs = scmNode.commits.find((commit) => commit.commit_date)?.commit_date;
      if (firstCommitTs) {
        entries.push({
          timestamp: new Date(firstCommitTs).getTime() - 1,
          lane: 'repo_header',
          id: `repo_header_${scmNode.id}`,
          label: scmNode.repository_name,
          prIndex,
        });
      }
    }

    scmNode.commits.forEach((commit) => {
      if (commit.commit_date) {
        const commitPreview = buildCommitPreview(commit);
        entries.push({
          timestamp: new Date(commit.commit_date).getTime(),
          lane: 'commit',
          id: `commit_${commit.id}`,
          // Screen-reader name for the card, which shows the hash and subject itself.
          label: `Commit${commitPreview.hash ? ` ${commitPreview.hash}` : ''}${
            commitPreview.subject ? `${commitPreview.hash ? ' ·' : ':'} ${commitPreview.subject}` : ''
          }`,
          parentId: `pr_${scmNode.id}`,
          prIndex,
          commitPreview,
        });
      }
    });
  });

  // PR comments — on the PR lifecycle lane
  data.pr_comment_nodes.forEach((comment) => {
    const crId = comment.change_request_id;
    const prIndex = prIndexMap.get(crId) ?? 0;
    entries.push({
      timestamp: new Date(comment.timestamp).getTime(),
      lane: 'pr_lifecycle',
      id: comment.id,
      label:
        (comment.loop_count ?? comment.count) > 1
          ? `${comment.loop_count ?? comment.count} comments added`
          : comment.label,
      parentId: `pr_lifecycle_${crId}`,
      prIndex,
      commentId: comment.id,
      commentPreview: buildCommentPreview(comment),
    });
  });

  // PR event logs — on the PR lifecycle lane
  data.pr_event_nodes.forEach((event) => {
    const crId = event.change_request_id;
    const prIndex = prIndexMap.get(crId) ?? 0;
    entries.push({
      timestamp: new Date(event.timestamp).getTime(),
      lane: 'pr_lifecycle',
      id: event.id,
      label: event.label,
      parentId: `pr_lifecycle_${crId}`,
      prIndex,
    });
  });

  // Sort chronologically
  entries.sort((a, b) => a.timestamp - b.timestamp);

  // === Per-lane Y positioning ===
  const yPositions: Map<string, number> = new Map();

  // Group entries by lane group key
  const laneGroups: Record<string, TimelineEntry[]> = {};
  entries.forEach((entry) => {
    const laneKey = entry.parentId ? `${entry.lane}_${entry.parentId}` : entry.lane;
    if (!laneGroups[laneKey]) laneGroups[laneKey] = [];
    laneGroups[laneKey].push(entry);
  });

  // Sessions, turn cards and commits share the space between task events.
  // Turn cards are bucketed by their session's start (`bucketTimestamp`) so they
  // stay in the session's gap even if a turn ran past the next task event.
  const taskEvents = entries.filter((entry) => entry.lane === 'task_event');
  const commitEntries = entries.filter((entry) => entry.lane === 'commit');
  // PR lifecycle items (opened/closed, comments, events) are positioned against
  // their PR's commits in step 3, but they also take a slot here so the gaps
  // leave room between commits for the items that fall between them.
  const prsWithCommits = new Set(commitEntries.map((commit) => commit.parentId?.replace(/^pr_/, '')));
  const isSlottedLifecycleItem = (entry: TimelineEntry) =>
    entry.lane === 'pr_lifecycle' && prsWithCommits.has(entry.parentId?.replace(/^pr_lifecycle_/, ''));
  const branchEntries = entries.filter((entry) => BRANCH_LANES.has(entry.lane) || isSlottedLifecycleItem(entry));
  const bucketTimestampOf = (entry: TimelineEntry) => entry.bucketTimestamp ?? entry.timestamp;

  const branchItemsInGap = (index: number): TimelineEntry[] =>
    branchEntries.filter((entry) => {
      const bucket = bucketTimestampOf(entry);
      // `!(>=)` rather than `<` so an unparseable (NaN) timestamp is excluded, as before.
      if (!(bucket >= taskEvents[index].timestamp)) return false;
      return index < taskEvents.length - 1 ? bucket < taskEvents[index + 1].timestamp : true;
    });

  // Items from before the first task event (e.g. sessions started before the
  // task was created) belong to no gap; they get a leading gap above it instead.
  // With no task events at all, every item goes there.
  const firstTaskEventTimestamp = taskEvents[0]?.timestamp ?? Number.POSITIVE_INFINITY;
  const leadingItems = branchEntries.filter((entry) => bucketTimestampOf(entry) < firstTaskEventTimestamp);

  // Gaps holding an expanded session use the time-block layout; every other gap
  // keeps the original evenly spaced layout, so collapsed graphs are unchanged.
  const gapItems = taskEvents.map((_, index) => branchItemsInGap(index));
  const sessionIntervals = entries
    .filter((entry) => entry.lane === 'agent_session' && !Number.isNaN(entry.timestamp))
    .map((entry) => ({ id: entry.id, startMs: entry.timestamp, endMs: entry.endTimestamp ?? entry.timestamp }));
  const layoutTemporalGapIfExpanded = (items: TimelineEntry[]) =>
    items.some((item) => item.turnTimings)
      ? layoutTemporalGap(
          items.filter((item) => item.lane !== 'pr_lifecycle'),
          sessionIntervals,
        )
      : null;
  const temporalGaps = gapItems.map(layoutTemporalGapIfExpanded);
  const leadingTemporalGap = layoutTemporalGapIfExpanded(leadingItems);
  const turnBlocks: TurnBlock[] = [];

  // Places a time-block gap's items and tracks relative to the gap's top.
  const placeTemporalGap = (temporal: ReturnType<typeof layoutTemporalGap>, gapTopY: number) => {
    temporal.offsets.forEach((offset, id) => {
      yPositions.set(id, gapTopY + offset);
    });
    temporal.blocks.forEach((block) => {
      turnBlocks.push({
        ...block,
        trackTop: gapTopY + block.trackTop,
        markers: block.markers.map((marker) => ({ ...marker, y: gapTopY + marker.y })),
        gapLabels: block.gapLabels.map((gap) => ({ ...gap, y: gapTopY + gap.y })),
      });
    });
  };

  // The leading gap is laid out from the top, first item at y 0, and pushes the
  // task events down, so expanding a pre-task session grows the graph downward
  // like every other gap instead of moving the session. Its last item sits one
  // row above the first task event. Gap layouts start their first item one row
  // below the gap's top, so the gap's top is -ROW_SPACING.
  const leadingGapHeight = leadingTemporalGap ? leadingTemporalGap.height : (leadingItems.length + 1) * ROW_SPACING;
  const leadingGapTop = -ROW_SPACING;
  const firstTaskEventY = leadingItems.length > 0 ? leadingGapTop + leadingGapHeight : 0;

  // 1. Task events: equal spacing, but leave gaps for agent sessions AND commits between them
  let taskY = firstTaskEventY;
  taskEvents.forEach((taskEvent, index) => {
    yPositions.set(taskEvent.id, taskY);

    if (index < taskEvents.length - 1) {
      const temporal = temporalGaps[index];
      // Gap = at least 1 row, plus extra for sessions + commits
      taskY += temporal ? temporal.height : Math.max(1, gapItems[index].length) * ROW_SPACING;
    }
  });

  // 2a. Leading gap: items one row apart from y 0, above the first task event.
  if (leadingTemporalGap) {
    placeTemporalGap(leadingTemporalGap, leadingGapTop);
  } else {
    const sortedLeadingItems = [...leadingItems].sort((a, b) => a.timestamp - b.timestamp);
    sortedLeadingItems.forEach((item, itemIndex) => {
      yPositions.set(item.id, leadingGapTop + (itemIndex + 1) * ROW_SPACING);
    });
  }

  // 2b. Position sessions + commits (and turn blocks) in task event gaps
  taskEvents.forEach((taskEvent, index) => {
    const taskEventY = yPositions.get(taskEvent.id) ?? 0;
    const temporal = temporalGaps[index];

    if (temporal) {
      placeTemporalGap(temporal, taskEventY);
      return;
    }

    const nextTaskEventY =
      index < taskEvents.length - 1
        ? (yPositions.get(taskEvents[index + 1].id) ?? taskEventY + ROW_SPACING)
        : taskEventY + ROW_SPACING * 3;
    const branchItems = [...gapItems[index]].sort((a, b) => a.timestamp - b.timestamp);

    if (branchItems.length > 0) {
      const availableSpace = nextTaskEventY - taskEventY;
      const itemSpacing = availableSpace / (branchItems.length + 1);

      branchItems.forEach((item, itemIndex) => {
        const itemY = taskEventY + itemSpacing * (itemIndex + 1);
        yPositions.set(item.id, itemY);
      });
    }
  });

  // 3. PR lifecycle lane: position items relative to commit Y positions
  // Lifecycle items that fall between commits get Y between those commits' Y values.
  // Items before all commits go above the first commit, items after go below the last.
  data.scm_nodes.forEach((scmNode) => {
    const lifecycleKey = `pr_lifecycle_pr_lifecycle_${scmNode.id}`;
    const lifecycleEntries = laneGroups[lifecycleKey] ?? [];
    const prCommits = commitEntries
      .filter((commit) => commit.parentId === `pr_${scmNode.id}`)
      .sort((a, b) => a.timestamp - b.timestamp);

    if (lifecycleEntries.length === 0) return;

    // If no commits, just space lifecycle items equally from the first task event
    if (prCommits.length === 0) {
      let prY = firstTaskEventY;
      lifecycleEntries.forEach((entry) => {
        yPositions.set(entry.id, prY);
        prY += ROW_SPACING;
      });
      return;
    }

    // Merge lifecycle + commits chronologically, then place each run of
    // lifecycle items against the commits around it. Every placement keeps the
    // run inside its bounds, so the PR column always reads in time order.
    const allPrItems = [
      ...lifecycleEntries.map((entry) => ({ entry, isCommit: false })),
      ...prCommits.map((entry) => ({ entry, isCommit: true })),
    ].sort((a, b) => a.entry.timestamp - b.entry.timestamp);

    let run: TimelineEntry[] = [];
    let previousCommitY: number | null = null;
    const placeRun = (nextCommitY: number | null) => {
      if (run.length === 0) return;
      if (previousCommitY !== null && nextCommitY !== null) {
        // Between two commits: spread evenly between them.
        const step = (nextCommitY - previousCommitY) / (run.length + 1);
        run.forEach((entry, index) => {
          yPositions.set(entry.id, (previousCommitY as number) + step * (index + 1));
        });
      } else if (previousCommitY !== null) {
        // After the last commit: stack downward from it.
        run.forEach((entry, index) => {
          yPositions.set(entry.id, (previousCommitY as number) + ROW_SPACING + PR_LIFECYCLE_STACK_STEP * index);
        });
      } else if (nextCommitY !== null) {
        // Before the first commit: stack upward from it, latest item nearest.
        run.forEach((entry, index) => {
          const fromCommit = run.length - 1 - index;
          yPositions.set(entry.id, nextCommitY - ROW_SPACING - PR_LIFECYCLE_STACK_STEP * fromCommit);
        });
      }
      run = [];
    };

    allPrItems.forEach((item) => {
      if (!item.isCommit) {
        run.push(item.entry);
        return;
      }
      const commitY = yPositions.get(item.entry.id) ?? 0;
      placeRun(commitY);
      previousCommitY = commitY;
    });
    placeRun(null);
  });

  // 4. Status nodes: aligned to closest task event Y
  entries
    .filter((entry) => entry.lane === 'status')
    .forEach((statusEntry) => {
      let closestY = 0;
      let closestDiff = Infinity;
      taskEvents.forEach((taskEvent) => {
        const diff = Math.abs(taskEvent.timestamp - statusEntry.timestamp);
        if (diff < closestDiff) {
          closestDiff = diff;
          closestY = yPositions.get(taskEvent.id) ?? 0;
        }
      });
      yPositions.set(statusEntry.id, closestY);
    });

  // 5. Repo header nodes: positioned above the first commit of their PR
  entries
    .filter((entry) => entry.lane === 'repo_header')
    .forEach((headerEntry) => {
      const prId = headerEntry.id.replace('repo_header_', '');
      const firstCommit = commitEntries.find((commit) => commit.parentId === `pr_${prId}`);
      if (firstCommit) {
        const firstCommitY = yPositions.get(firstCommit.id) ?? 0;
        yPositions.set(headerEntry.id, firstCommitY - ROW_SPACING * 0.6);
      }
    });

  return { entries, yPositions, turnBlocks };
};

/** Interactive node kind for each lane that has one. */
const INTERACTIVE_KIND_BY_LANE: Partial<Record<string, InteractiveNodeKind>> = {
  agent_session: InteractiveNodeKind.Session,
  agent_turn: InteractiveNodeKind.Turn,
  agent_turn_more: InteractiveNodeKind.MoreTurns,
};

/**
 * Build ReactFlow nodes and edges. Y positions are timestamp-driven.
 *
 * @param {UnifiedProcessGraphData} data - The graph data from the API
 * @param {ReadonlyMap<string, TurnExpansion>} turnExpansion - Expanded sessions, keyed by session node id; empty = all collapsed
 * @returns {{ nodes: ReactFlowNode[], edges: Edge[] }}
 */
const buildFlowGraph = (
  data: UnifiedProcessGraphData,
  turnExpansion: ReadonlyMap<string, TurnExpansion> = new Map(),
): { nodes: ReactFlowNode[]; edges: Edge[] } => {
  const nodes: ReactFlowNode[] = [];
  const edges: Edge[] = [];
  const { entries, yPositions, turnBlocks } = buildTimeline(data, turnExpansion);
  const turnCountBySessionNodeId = new Map(
    data.agent_session_nodes.map((session) => [session.id, session.turns.length]),
  );

  // Build nodes per timeline entry — dot with label to the right
  entries.forEach((entry) => {
    const yPosition = yPositions.get(entry.id) ?? 0;
    const laneStyle = LANE_STYLES[entry.lane];
    if (!laneStyle) return;
    const isTurnLane = TURN_LANES.has(entry.lane);
    const isChildLane = entry.lane === 'commit' || isTurnLane;
    const prOffset = (entry.prIndex ?? 0) * PR_COLUMN_WIDTH;
    const usesPrOffset = entry.lane === 'commit' || entry.lane === 'pr_lifecycle' || entry.lane === 'repo_header';
    const xPosition = laneStyle.x + (usesPrOffset ? prOffset : 0);
    const interactiveKind = entry.commentId ? InteractiveNodeKind.Comment : INTERACTIVE_KIND_BY_LANE[entry.lane];
    // Commit cards take focus themselves to show their hover preview, so the
    // ReactFlow wrapper must not add a second tab stop.
    const ownsFocus = Boolean(interactiveKind || entry.commitPreview);

    nodes.push({
      id: entry.id,
      type: 'dotNode',
      position: { x: xPosition, y: yPosition },
      // Interactive labels are the focusable buttons; ReactFlow's own wrapper
      // would add a second, outline-less tab stop that can't act on the node.
      ...(ownsFocus && { focusable: false }),
      data: {
        label: entry.label,
        color: laneStyle.bg,
        fontSize: isChildLane ? 11 : 12,
        icon: entry.icon,
        ...(isTurnLane && { labelMaxWidth: TURN_LABEL_MAX_WIDTH }),
        ...(entry.commitPreview && { commitPreview: entry.commitPreview, labelMaxWidth: COMMIT_LABEL_MAX_WIDTH }),
        ...(interactiveKind && {
          clickable: true,
          interactiveKind,
          ...(entry.sessionId && {
            sessionId: entry.sessionId,
            sessionNodeId: interactiveKind === InteractiveNodeKind.Session ? entry.id : entry.parentId,
          }),
          ...(entry.turnId && { turnId: entry.turnId }),
          ...(entry.commentId && { commentId: entry.commentId }),
          ...(entry.commentPreview && { commentPreview: entry.commentPreview }),
        }),
        ...(interactiveKind === InteractiveNodeKind.Session && {
          turnCount: turnCountBySessionNodeId.get(entry.id) ?? 0,
          turnsExpanded: turnExpansion.has(entry.id),
        }),
      },
    });
  });

  // Build edges within each lane group. Turn cards are joined to the time track
  // by leader lines instead (below), so they are left out of lane chaining. Repo
  // headers each label their own PR's commit column, so they are not chained either.
  const laneGroups: Record<string, TimelineEntry[]> = {};
  entries.forEach((entry) => {
    if (TURN_LANES.has(entry.lane) || entry.lane === 'repo_header') return;
    const laneKey = entry.parentId ? `${entry.lane}_${entry.parentId}` : entry.lane;
    if (!laneGroups[laneKey]) laneGroups[laneKey] = [];
    laneGroups[laneKey].push(entry);
  });

  Object.entries(laneGroups).forEach(([laneKey, laneEntries]) => {
    for (let index = 1; index < laneEntries.length; index++) {
      const strokeColor = LANE_STYLES[laneEntries[index].lane]?.bg ?? '#666';
      const cycleLabel = formatElapsed(laneEntries[index].timestamp - laneEntries[index - 1].timestamp);

      edges.push({
        id: `edge-${laneKey}-${index}`,
        source: laneEntries[index - 1].id,
        target: laneEntries[index].id,
        label: cycleLabel,
        markerEnd: { type: MarkerType.Arrow, color: 'transparent' },
        style: { stroke: strokeColor, strokeWidth: 1.5 },
        labelStyle: { fontSize: 10, fill: '#666' },
        labelBgStyle: { fill: 'white', fillOpacity: 0.8 },
        labelBgPadding: [2, 4] as [number, number],
        labelBgBorderRadius: 3,
      });
    }
  });

  // Expanded sessions: draw the track (a thin rail hung from the session dot),
  // a square marker per turn joined to its card, and the time between steps.
  const turnColor = LANE_STYLES.agent_turn.bg;
  turnBlocks.forEach((block) => {
    const trackId = `turn_track_${block.sessionNodeId}`;
    nodes.push({
      id: trackId,
      type: 'turnTrack',
      position: { x: TRACK_CENTER_X - TRACK_WIDTH / 2, y: block.trackTop },
      selectable: false,
      focusable: false,
      data: { height: block.trackHeight, width: TRACK_WIDTH, color: turnColor },
    });
    // The session lane chains every session to the next along the session dot's
    // x, which is exactly where the track runs. Leave from the track's bottom
    // instead, so that line (and its cycle-time label) doesn't sit on the track.
    edges.forEach((edge, edgeIndex) => {
      if (edge.id.startsWith('edge-agent_session-') && edge.source === block.sessionNodeId) {
        edges[edgeIndex] = { ...edge, source: trackId };
      }
    });
    edges.push({
      id: `session-to-track-${block.sessionNodeId}`,
      source: block.sessionNodeId,
      target: trackId,
      type: 'straight',
      style: { stroke: turnColor, strokeWidth: 1.5 },
    });

    block.markers.forEach((marker) => {
      const markerId = `${TURN_MARKER_NODE_PREFIX}${marker.cardId}`;
      const isTurn = marker.turnNumber !== undefined;
      nodes.push({
        id: markerId,
        type: 'turnMarker',
        position: { x: TRACK_CENTER_X - TRACK_MARKER_SIZE / 2, y: marker.y - TRACK_MARKER_SIZE / 2 },
        selectable: false,
        focusable: false,
        data: {
          size: TRACK_MARKER_SIZE,
          color: turnColor,
          hollow: !isTurn,
          ...(isTurn && {
            markerTurnId: marker.turnId,
            sessionId: marker.sessionId,
            title: `Turn ${turnDisplayNumber(marker.turnNumber ?? 0)} \u00b7 ${fmtDuration(marker.durationMs)}`,
          }),
        },
      });
      edges.push({
        id: `turn-leader-${marker.cardId}`,
        source: markerId,
        sourceHandle: 'right',
        target: marker.cardId,
        targetHandle: 'left',
        type: 'straight',
        style: { stroke: turnColor, strokeWidth: 1, opacity: 0.7 },
      });
    });

    block.gapLabels.forEach((gap, index) => {
      nodes.push({
        id: `turn_gap_${block.sessionNodeId}_${index}`,
        type: 'trackGapLabel',
        position: { x: TRACK_CENTER_X + TRACK_GAP_LABEL_OFFSET_X, y: gap.y - TRACK_GAP_LABEL_HEIGHT / 2 },
        selectable: false,
        focusable: false,
        data: { label: gap.label, isLong: gap.isLong, color: turnColor },
      });
    });
  });

  // PR lifecycle ↔ commits: weave the PR lifecycle edge through commits
  // Between any two consecutive PR lifecycle items, if commits occur, route through handoff nodes
  const prLifecycleEdgesToRemove = new Set<string>();
  const entryPrIndexMap: Map<string, number> = new Map();
  entries.forEach((entry) => {
    if (entry.prIndex !== undefined) entryPrIndexMap.set(entry.id, entry.prIndex);
  });

  // The orange links between PR lifecycle items and commits carry the time between
  // their ends, styled like the lane edge labels.
  const detourLabel = (fromMs: number, toMs: number) => ({
    label: formatElapsed(toMs - fromMs),
    labelStyle: { fontSize: 10, fill: '#666' },
    labelBgStyle: { fill: 'white', fillOpacity: 0.8 },
    labelBgPadding: [2, 4] as [number, number],
    labelBgBorderRadius: 3,
  });

  data.scm_nodes.forEach((scmNode) => {
    const lifecycleGroupKey = `pr_lifecycle_pr_lifecycle_${scmNode.id}`;
    const lifecycleEntries = laneGroups[lifecycleGroupKey] ?? [];
    const prCommits = entries
      .filter((entry) => entry.lane === 'commit' && entry.parentId === `pr_${scmNode.id}`)
      .sort((a, b) => a.timestamp - b.timestamp);

    if (lifecycleEntries.length === 0 || prCommits.length === 0) return;

    // Connect the last commit before PR opened to the PR opened node
    const firstLifecycle = lifecycleEntries[0];
    const lastCommitBeforePr = prCommits.filter((commit) => commit.timestamp < firstLifecycle.timestamp).at(-1);

    if (lastCommitBeforePr) {
      const commitY = yPositions.get(lastCommitBeforePr.id) ?? 0;
      const prOffset = (entryPrIndexMap.get(lastCommitBeforePr.id) ?? 0) * PR_COLUMN_WIDTH;
      const handoffId = `pr_handoff_${lastCommitBeforePr.id}`;
      if (!nodes.some((node) => node.id === handoffId)) {
        nodes.push({
          id: handoffId,
          type: 'dotNode',
          position: { x: PR_HANDOFF_X + prOffset, y: commitY },
          data: { label: '', color: '#E65100' },
        });
      }

      edges.push({
        id: `pre-pr-commit-to-opened-${scmNode.id}`,
        source: handoffId,
        target: firstLifecycle.id,
        type: 'step',
        markerEnd: { type: MarkerType.Arrow, color: 'transparent' },
        style: { stroke: '#E65100', strokeWidth: 1.5 },
        ...detourLabel(lastCommitBeforePr.timestamp, firstLifecycle.timestamp),
      });
    }

    // For each pair of consecutive lifecycle items, find commits between them
    for (let index = 1; index < lifecycleEntries.length; index++) {
      const prevLifecycle = lifecycleEntries[index - 1];
      const nextLifecycle = lifecycleEntries[index];
      const commitsBetween = prCommits.filter(
        (commit) => commit.timestamp >= prevLifecycle.timestamp && commit.timestamp <= nextLifecycle.timestamp,
      );

      if (commitsBetween.length === 0) continue;

      // Remove the direct lifecycle→lifecycle edge
      prLifecycleEdgesToRemove.add(`edge-${lifecycleGroupKey}-${index}`);

      // Create handoff nodes next to each commit
      commitsBetween.forEach((commitEntry) => {
        const commitY = yPositions.get(commitEntry.id) ?? 0;
        const prOffset = (entryPrIndexMap.get(commitEntry.id) ?? 0) * PR_COLUMN_WIDTH;
        const handoffId = `pr_handoff_${commitEntry.id}`;
        if (!nodes.some((node) => node.id === handoffId)) {
          nodes.push({
            id: handoffId,
            type: 'dotNode',
            position: { x: PR_HANDOFF_X + prOffset, y: commitY },
            data: { label: '', color: '#E65100' },
          });
        }
      });

      // Edge: prevLifecycle → first handoff
      edges.push({
        id: `pr-detour-start-${prevLifecycle.id}-${index}`,
        source: prevLifecycle.id,
        target: `pr_handoff_${commitsBetween[0].id}`,
        type: 'step',
        markerEnd: { type: MarkerType.Arrow, color: 'transparent' },
        style: { stroke: '#E65100', strokeWidth: 1.5 },
        ...detourLabel(prevLifecycle.timestamp, commitsBetween[0].timestamp),
      });

      // Chain handoffs
      for (let commitIndex = 1; commitIndex < commitsBetween.length; commitIndex++) {
        edges.push({
          id: `pr-handoff-chain-${commitsBetween[commitIndex].id}-${index}`,
          source: `pr_handoff_${commitsBetween[commitIndex - 1].id}`,
          target: `pr_handoff_${commitsBetween[commitIndex].id}`,
          type: 'step',
          markerEnd: { type: MarkerType.Arrow, color: 'transparent' },
          style: { stroke: '#E65100', strokeWidth: 1.5 },
        });
      }

      // Edge: last handoff → nextLifecycle
      edges.push({
        id: `pr-detour-end-${nextLifecycle.id}-${index}`,
        source: `pr_handoff_${commitsBetween[commitsBetween.length - 1].id}`,
        target: nextLifecycle.id,
        type: 'step',
        markerEnd: { type: MarkerType.Arrow, color: 'transparent' },
        style: { stroke: '#E65100', strokeWidth: 1.5 },
        ...detourLabel(commitsBetween[commitsBetween.length - 1].timestamp, nextLifecycle.timestamp),
      });
    }
  });

  // Status nodes — label on source node (left side), Y aligned with task events
  const taskEventEntriesForStatus = entries.filter((entry) => entry.lane === 'task_event');
  const findClosestTaskEventY = (timestamp: string): number => {
    const target = new Date(timestamp).getTime();
    let closestY = 0;
    let closestDiff = Infinity;
    taskEventEntriesForStatus.forEach((entry) => {
      const diff = Math.abs(entry.timestamp - target);
      if (diff < closestDiff) {
        closestDiff = diff;
        closestY = yPositions.get(entry.id) ?? 0;
      }
    });
    return closestY;
  };

  const DEFAULT_STATUS_COLOR = '#4CAF50';
  const renderedStatusNodes = new Set<string>();

  data.status_edges.forEach((statusEdge, index) => {
    const sourceStatusNode = data.status_nodes.find((node) => node.id === statusEdge.source);
    const targetStatusNode = data.status_nodes.find((node) => node.id === statusEdge.target);
    if (!sourceStatusNode || !targetStatusNode) return;

    const sectionColor = (statusEdge.section && PHASE_COLORS[statusEdge.section]) || DEFAULT_STATUS_COLOR;

    const sourceY = findClosestTaskEventY(sourceStatusNode.timestamp);
    const targetY = Math.max(findClosestTaskEventY(targetStatusNode.timestamp), sourceY + ROW_SPACING);

    // Cycle time between status transitions
    const sourceMs = new Date(sourceStatusNode.timestamp).getTime();
    const targetMs = new Date(targetStatusNode.timestamp).getTime();
    const cycleLabel = formatElapsed(targetMs - sourceMs);

    if (!renderedStatusNodes.has(statusEdge.source)) {
      nodes.push({
        id: statusEdge.source,
        type: 'dotNode',
        position: { x: STATUS_X, y: sourceY },
        data: { label: statusEdge.label, color: sectionColor, labelSide: 'left', fontSize: 11 },
      });
      renderedStatusNodes.add(statusEdge.source);
    }
    if (!renderedStatusNodes.has(statusEdge.target)) {
      nodes.push({
        id: statusEdge.target,
        type: 'dotNode',
        position: { x: STATUS_X, y: targetY },
        data: { label: '', color: sectionColor },
      });
      renderedStatusNodes.add(statusEdge.target);
    }

    edges.push({
      id: `status-edge-${index}`,
      source: statusEdge.source,
      target: statusEdge.target,
      label: cycleLabel,
      markerEnd: { type: MarkerType.Arrow, color: 'transparent' },
      style: { stroke: sectionColor, strokeWidth: 2 },
      labelStyle: { fontSize: 10, fill: '#666' },
      labelBgStyle: { fill: 'white', fillOpacity: 0.8 },
      labelBgPadding: [2, 4] as [number, number],
      labelBgBorderRadius: 3,
    });
  });

  // Cross-lane edges: agent turns → commits (dashed curved)
  (data.cross_lane_edges ?? []).forEach((crossEdge) => {
    // Only render if both source and target exist in the graph
    const sourceExists = nodes.some((node) => node.id === crossEdge.source);
    const targetExists = nodes.some((node) => node.id === crossEdge.target);
    if (!sourceExists || !targetExists) return;

    edges.push({
      id: crossEdge.id,
      source: crossEdge.source,
      target: crossEdge.target,
      label: crossEdge.label || undefined,
      type: 'smoothstep',
      markerEnd: { type: MarkerType.Arrow, color: 'transparent' },
      style: { stroke: '#999', strokeWidth: 1.5, strokeDasharray: '6 3' },
      labelStyle: { fontSize: 9, fill: '#999' },
      labelBgStyle: { fill: 'white', fillOpacity: 0.9 },
      labelBgPadding: [2, 4] as [number, number],
      labelBgBorderRadius: 3,
    });
  });

  // Unified task handoff: single branch for all activity (turns + commits) between task events
  const taskEventEntries = entries.filter((entry) => entry.lane === 'task_event');
  const handoffEdgesToRemove = new Set<string>();
  const isHandoffLane = (entry: TimelineEntry) => entry.lane === 'agent_session' || entry.lane === 'commit';

  /**
   * Adds a handoff dot beside each branch item and chains them top to bottom.
   * Collapsed gaps place items in time order, so this is chronological there;
   * in a gap with a time block it follows the layout, since deferred sessions
   * sit below commits that happened during the block.
   *
   * @param {TimelineEntry[]} branchItems - The gap's sessions and commits (non-empty)
   * @returns {{ firstHandoffId: string, lastHandoffId: string, sorted: TimelineEntry[] }} The chain's ends and items, top to bottom
   */
  const addHandoffChain = (branchItems: TimelineEntry[]) => {
    const sorted = [...branchItems].sort(
      (a, b) => (yPositions.get(a.id) ?? 0) - (yPositions.get(b.id) ?? 0) || a.timestamp - b.timestamp,
    );

    // Create handoff nodes — X position depends on type, offset by prIndex for commits
    sorted.forEach((item) => {
      const itemY = yPositions.get(item.id) ?? 0;
      const prOffset = item.lane === 'commit' ? (item.prIndex ?? 0) * PR_COLUMN_WIDTH : 0;
      const handoffX = (item.lane === 'agent_session' ? HANDOFF_X : COMMIT_HANDOFF_X) + prOffset;
      const handoffColor = '#2196F3';
      nodes.push({
        id: `handoff_${item.id}`,
        type: 'dotNode',
        position: { x: handoffX, y: itemY },
        data: { label: '', color: handoffColor },
      });
    });

    // Chain edges between consecutive handoffs
    for (let index = 1; index < sorted.length; index++) {
      edges.push({
        id: `handoff-chain-${sorted[index].id}`,
        source: `handoff_${sorted[index - 1].id}`,
        target: `handoff_${sorted[index].id}`,
        type: 'step',
        markerEnd: { type: MarkerType.Arrow, color: 'transparent' },
        style: { stroke: '#2196F3', strokeWidth: 1.5 },
      });
    }

    return {
      firstHandoffId: `handoff_${sorted[0].id}`,
      lastHandoffId: `handoff_${sorted[sorted.length - 1].id}`,
      sorted,
    };
  };

  // Sessions + commits from before the first task event chain down into it.
  // With no task events there is nothing to hand off to, so no chain is drawn.
  const firstTaskEvent = taskEventEntries[0];
  const leadingBranchItems = firstTaskEvent
    ? entries.filter((entry) => isHandoffLane(entry) && entry.timestamp < firstTaskEvent.timestamp)
    : [];
  if (firstTaskEvent && leadingBranchItems.length > 0) {
    const { lastHandoffId, sorted } = addHandoffChain(leadingBranchItems);
    edges.push({
      id: `handoff-to-task-${sorted[sorted.length - 1].id}`,
      source: lastHandoffId,
      target: firstTaskEvent.id,
      type: 'step',
      markerEnd: { type: MarkerType.Arrow, color: 'transparent' },
      style: { stroke: '#2196F3', strokeWidth: 1.5 },
    });
  }

  // For each pair of consecutive task events, collect all branch items and chain them
  taskEventEntries.forEach((taskEvent, taskIndex) => {
    const nextTaskEvent = taskIndex < taskEventEntries.length - 1 ? taskEventEntries[taskIndex + 1] : null;

    // Collect all sessions + commits between this task event and the next
    const gapBranchItems = entries.filter((entry) => {
      if (!isHandoffLane(entry)) return false;
      if (nextTaskEvent) {
        return entry.timestamp >= taskEvent.timestamp && entry.timestamp < nextTaskEvent.timestamp;
      }
      return entry.timestamp >= taskEvent.timestamp;
    });

    if (gapBranchItems.length === 0) return;

    const { firstHandoffId, lastHandoffId, sorted: branchItems } = addHandoffChain(gapBranchItems);

    // Edge: taskEvent → first handoff
    edges.push({
      id: `task-to-handoff-${branchItems[0].id}`,
      source: taskEvent.id,
      target: firstHandoffId,
      type: 'step',
      markerEnd: { type: MarkerType.Arrow, color: 'transparent' },
      style: { stroke: '#2196F3', strokeWidth: 1.5 },
    });

    // Edge: last handoff → next task event
    if (nextTaskEvent) {
      edges.push({
        id: `handoff-to-task-${branchItems[branchItems.length - 1].id}`,
        source: lastHandoffId,
        target: nextTaskEvent.id,
        type: 'step',
        markerEnd: { type: MarkerType.Arrow, color: 'transparent' },
        style: { stroke: '#2196F3', strokeWidth: 1.5 },
      });
      handoffEdgesToRemove.add(`task-event-edge-${nextTaskEvent.id}`);
    }
  });

  // Remove replaced edges (task handoffs + PR lifecycle direct edges)
  const filteredEdges = edges.filter(
    (edge) => !handoffEdgesToRemove.has(edge.id) && !prLifecycleEdgesToRemove.has(edge.id),
  );

  return { nodes, edges: filteredEdges };
};

export { buildFlowGraph, computeOverviewMetrics, formatElapsed, toAgentSessionId };
